import React from 'react';

interface Props {
  children: React.ReactNode;
  /** Rendered instead of the crashed subtree. Receives the error's message. */
  fallback?: (message: string) => React.ReactNode;
  /** Optional label to help identify where a crash happened in logs. */
  label?: string;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time crashes in a subtree so one malformed record (e.g. a
 * legacy or imported campaign whose stored shape predates the current editor)
 * shows a friendly message instead of blanking the whole page. Scope it to the
 * smallest risky area so the rest of the page keeps working.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface to the console so it still shows up in logs/monitoring.
    console.error(`ErrorBoundary${this.props.label ? ` [${this.props.label}]` : ''}:`, error, info);
  }

  render() {
    if (this.state.error) {
      const message = this.state.error.message || 'Something went wrong rendering this section.';
      return this.props.fallback ? this.props.fallback(message) : null;
    }
    return this.props.children;
  }
}
